import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { filesApi, getCachedBlobUrl, createBlobUrl, triggerDownload } from '../../api/files';
import { chatsApi, ChatType } from '../../api/chats';
import { useAvatarUrl } from '../../hooks/useAvatarUrl';
import HrnbImage from '../controls/HrnbImage';
import ImagePreviewModal from './ImagePreviewModal';
import DuelInviteAttachment from './DuelInviteAttachment';
import DuelInviteFromMessage from './DuelInviteFromMessage';
import BattleInviteAttachment from './BattleInviteAttachment';
import AudioPlayer from './AudioPlayer';
import LinkPreview from './LinkPreview';
import PollMessage from './PollMessage';
import GroupReadReceipt from './GroupReadReceipt';
import { extractUrls } from '../../utils/linkPreview';
import { renderMarkdownLite } from '../../utils/markdownLite';
import { normalizeReactions, findMyReaction } from '../../utils/reactions';
import EmojiPicker from './EmojiPicker';
import '../../css/chats/ChatMessage.css';

/**
 * SVG-иконка файла
 */
function FileIcon({ className }) {
    return (
        <svg
            className={className}
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
        </svg>
    );
}

/**
 * Вложение в сообщении
 * Получает метаданные файла через GET /api/files/{id}:
 * - Если fileType === 'IMAGE' или расширение изображения (png/jpg/bmp/gif/webp/svg) — загружаем blob через GET /api/files/{id}/download и отображаем
 * - Текстовые, аудио и прочие файлы — иконка файла со ссылкой на скачивание
 * - Если это приглашение (invitationType) — отображаем соответствующий компонент:
 *   DUEL_INVITE → DuelInviteAttachment, BATTLE_INVITE → BattleInviteAttachment
 */
function MessageAttachment({ attachment, isOwn, onInviteAction }) {
    const [fileInfo, setFileInfo] = useState(null);
    const [imageUrl, setImageUrl] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [videoUrl, setVideoUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [downloading, setDownloading] = useState(false);

    // Проверка на приглашение (V2: InvitationType — DUEL_INVITE или BATTLE_INVITE)
    const isInviteAttachment = attachment?.invitationType === 'DUEL_INVITE'
        || attachment?.invitationType === 'BATTLE_INVITE';

    // attachment может быть объектом с fileId или id, или нормализованным объектом
    const fileId = attachment?.fileId || attachment?.id || attachment;
    const attachmentType = attachment?.type || attachment?.fileType;
    const attachmentFileName = attachment?.fileName || attachment?.filename;
    const attachmentMimeType = attachment?.mimeType || attachment?.contentType;

    /**
     * Скачивание файла с оригинальным именем.
     * Использует API для получения blob и triggerDownload для сохранения.
     * Для изображений и аудио переиспользует кэшированный blob, если есть.
     */
    const handleDownload = useCallback(async (e) => {
        e?.stopPropagation?.();
        e?.preventDefault?.();
        if (!fileId || downloading) return;

        const filename = fileInfo?.filename || attachmentFileName || 'Файл';
        setDownloading(true);
        try {
            // Проверяем кэш blob (для изображений и аудио blob уже загружен)
            const cached = getCachedBlobUrl(fileId);
            if (cached?.blob) {
                triggerDownload(cached.blob, filename);
                return;
            }

            // Скачиваем через универсальный endpoint
            const blob = await filesApi.download(fileId);
            triggerDownload(blob, filename);
        } catch (err) {
            console.error('[MessageAttachment] Download failed:', err);
            // Fallback: открываем прямую ссылку в новой вкладке
            window.open(`/api/files/${fileId}/download`, '_blank');
        } finally {
            setDownloading(false);
        }
    }, [fileId, fileInfo, attachmentFileName, downloading]);

    // Загрузка информации о файле
    useEffect(() => {
        let cancelled = false;

        if (isInviteAttachment || !fileId) {
            setLoading(false);
            return;
        }

        // Если метаданные уже предоставлены с конкретным медиа-типом — используем их.
        // Для дефолтного 'FILE' (UUID без метаданных) запрашиваем реальные метаданные
        // через API, чтобы корректно определить IMAGE/AUDIO/VIDEO.
        const MEDIA_TYPES = ['IMAGE', 'AUDIO', 'VIDEO'];
        if (MEDIA_TYPES.includes(attachmentType) && attachmentFileName) {
            const contentType = attachmentMimeType || '';
            const filename = attachmentFileName;
            const fileType = attachmentType;
            if (!cancelled) {
                setFileInfo({ contentType, filename, fileType });
                setLoading(false);
            }
            return;
        }
        const fetchFileInfo = async () => {
            try {
                const data = await filesApi.get(fileId);
                const fileData = data?.file || data;
                // API возвращает: type (IMAGE, AUDIO...), mimeType, fileName, originalName
                // Нормализуем к единому формату
                const contentType = fileData?.mimeType || fileData?.contentType || '';
                const filename = fileData?.fileName || fileData?.originalName || 'Файл';
                const fileType = fileData?.type || fileData?.fileType || '';
                if (!cancelled) {
                    setFileInfo({ contentType, filename, fileType });
                }
            } catch (err) {
                console.error(`[MessageAttachment] Failed to fetch file info for ${fileId}:`, err);
                if (!cancelled) {
                    setFileInfo({ contentType: '', filename: 'Файл', fileType: '' });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchFileInfo();

        return () => {
            cancelled = true;
        };
    }, [fileId, isInviteAttachment, attachmentType, attachmentFileName, attachmentMimeType]);

    // Загрузка медиа (изображение, аудио или видео)
    useEffect(() => {
        if (!fileInfo || isInviteAttachment || !fileId) return;
        let cancelled = false;

        // Сначала проверяем по типу файла (IMAGE/AUDIO/VIDEO), затем по contentType, затем по расширению
        const isImageByType = fileInfo.fileType === 'IMAGE';
        const isAudioByType = fileInfo.fileType === 'AUDIO';
        const isVideoByType = fileInfo.fileType === 'VIDEO';

        const isImageByMime = !isImageByType && fileInfo.contentType.startsWith('image/');
        const isAudioByMime = !isAudioByType && fileInfo.contentType.startsWith('audio/');
        const isVideoByMime = !isVideoByType && fileInfo.contentType.startsWith('video/');

        const isImageByName = !isImageByType && !isImageByMime && /\.(png|jpe?g|bmp|gif|webp|svg)$/i.test(fileInfo.filename);
        const isAudioByName = !isAudioByType && !isAudioByMime && /\.(mp3|wav|ogg|flac|aac|m4a)$/i.test(fileInfo.filename);
        const isVideoByName = !isVideoByType && !isVideoByMime && /\.(mp4|webm|ogg|mov|avi|mkv|wmv|flv|m4v)$/i.test(fileInfo.filename);

        const isImage = isImageByType || isImageByMime || isImageByName;
        const isAudio = isAudioByType || isAudioByMime || isAudioByName;
        const isVideo = isVideoByType || isVideoByMime || isVideoByName;

        if (!isImage && !isAudio && !isVideo) {
            return;
        }

        const fetchMediaBlob = async () => {
            try {
                // Проверяем кэш перед запросом.
                // Кэшированный blob уже нормализован (правильный MIME),
                // поэтому переиспользуем готовый URL без создания нового.
                const cached = getCachedBlobUrl(fileId);
                if (cached) {
                    if (isAudio) {
                        if (!cancelled) setAudioUrl(cached.url);
                    } else if (isImage) {
                        if (!cancelled) setImageUrl(cached.url);
                    } else if (isVideo) {
                        if (!cancelled) setVideoUrl(cached.url);
                    }
                    if (!cancelled) setLoading(false);
                    return;
                }

                let blob;
                if (isAudio) {
                    // Используем retry logic с задержкой для 429 ошибок
                    blob = await filesApi.downloadAudio(fileId, { retries: 5, retryDelay: 2000 });

                    console.log('[MessageAttachment] Audio downloaded:', {
                        fileId,
                        blobType: blob.type,
                        blobSize: blob.size,
                        fileInfoContentType: fileInfo.contentType,
                        fileInfoFileType: fileInfo.fileType,
                        filename: fileInfo.filename
                    });

                    if (blob.size === 0) {
                        if (!cancelled) setError(true);
                        return;
                    }

                    // Создаём новый blob с правильным MIME-type и кэшируем
                    // Приоритет: тип из ответа сервера (blob.type) > метаданные > fallback
                    const audioMime = blob.type || fileInfo.contentType || 'audio/mpeg';
                    const audioBlob = new Blob([blob], { type: audioMime });
                    const url = createBlobUrl(fileId, audioBlob);
                    if (!cancelled) setAudioUrl(url);
                } else if (isImage) {
                    // Используем retry logic с задержкой для 429 ошибок
                    blob = await filesApi.downloadImage(fileId, { retries: 5, retryDelay: 2000 });

                    if (blob.size === 0) {
                        if (!cancelled) setError(true);
                        return;
                    }

                    // Создаём blob с правильным MIME-type и кэшируем URL
                    // для переиспользования при повторном монтировании компонента
                    const imageBlob = new Blob([blob], { type: fileInfo.contentType || 'image/jpeg' });
                    const url = createBlobUrl(fileId, imageBlob);
                    if (!cancelled) setImageUrl(url);
                } else if (isVideo) {
                    // Используем retry logic с задержкой для 429 ошибок
                    blob = await filesApi.downloadVideo(fileId, { retries: 5, retryDelay: 2000 });

                    console.log('[MessageAttachment] Video downloaded:', {
                        fileId,
                        blobType: blob.type,
                        blobSize: blob.size,
                        fileInfoContentType: fileInfo.contentType,
                        fileInfoFileType: fileInfo.fileType,
                        filename: fileInfo.filename
                    });

                    if (blob.size === 0) {
                        if (!cancelled) setError(true);
                        return;
                    }

                    // Создаём blob с правильным MIME-type и кэшируем
                    const videoMime = blob.type || fileInfo.contentType || 'video/mp4';
                    const videoBlob = new Blob([blob], { type: videoMime });
                    const url = createBlobUrl(fileId, videoBlob);
                    if (!cancelled) setVideoUrl(url);
                }
            } catch (err) {
                // Не логируем 429 ошибки как error — это ожидаемая ситуация при rate limiting
                if (err.response?.status !== 429) {
                    console.error('[MessageAttachment] Failed to load media blob:', err.message);
                }
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchMediaBlob();

        return () => {
            cancelled = true;
            // URL управляются кэшем blobCache (TTL 5 мин) — не отзываем вручную,
            // чтобы переиспользовать их при повторном монтировании компонента
            // (скролл, ререндер списка сообщений) и избежать повторных загрузок.
        };
    }, [fileInfo, fileId, isInviteAttachment]);

    // Если это приглашение — отображаем соответствующий компонент по типу
    if (isInviteAttachment) {
        if (attachment?.invitationType === 'BATTLE_INVITE') {
            return (
                <BattleInviteAttachment
                    attachment={attachment}
                    isOwn={isOwn}
                    onAction={onInviteAction}
                />
            );
        }
        return (
            <DuelInviteAttachment
                attachment={attachment}
                isOwn={isOwn}
                onAction={onInviteAction}
            />
        );
    }

    // Если нет fileId — ничего не показываем
    if (!fileId) {
        return null;
    }

    if (loading) {
        return (
            <div className="chat-message__attachment chat-message__attachment--loading">
                <div className="chat-message__attachment-spinner">⏳</div>
            </div>
        );
    }

    if (error) {
        // Fallback: показываем как обычный файл с кнопкой скачивания
        return (
            <div className="chat-message__attachment chat-message__attachment--file">
                <button
                    type="button"
                    className="chat-message__attachment-file"
                    onClick={handleDownload}
                    disabled={downloading}
                    title={fileInfo?.filename || 'Файл'}
                >
                    <FileIcon className="chat-message__attachment-icon" />
                    <span className="chat-message__attachment-filename">{fileInfo?.filename || 'Файл'}</span>
                    {downloading && <span className="chat-message__attachment-download-spinner">⏳</span>}
                </button>
            </div>
        );
    }

    // Отображение аудио (ПРОВЕРЯЕМ ПЕРЕД ИЗОБРАЖЕНИЕМ!)
    if (audioUrl) {
        return (
            <AudioPlayer
                audioUrl={audioUrl}
                fileName={fileInfo?.filename}
                fileId={fileId}
                onDownload={handleDownload}
            />
        );
    }

    // Отображение видео (проверяем перед изображением!)
    if (videoUrl) {
        return (
            <div className="chat-message__attachment chat-message__attachment--video">
                <video
                    src={videoUrl}
                    controls
                    preload="metadata"
                    className="chat-message__video"
                    playsInline
                />
                <button
                    type="button"
                    className="chat-message__attachment-download-btn"
                    onClick={handleDownload}
                    disabled={downloading}
                    title="Скачать видео"
                    aria-label="Скачать видео"
                >
                    {downloading ? '⏳' : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                             fill="none" stroke="currentColor" strokeWidth="2"
                             strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                    )}
                </button>
            </div>
        );
    }

    // Отображение изображения
    if (imageUrl) {
        return (
            <>
                <div className="chat-message__attachment chat-message__attachment--image" onClick={() => setPreviewOpen(true)} style={{ cursor: 'zoom-in' }}>
                    <img
                        src={imageUrl}
                        alt={fileInfo?.filename || 'Изображение'}
                        className="chat-message__attachment-img"
                        loading="lazy"
                    />
                    <button
                        type="button"
                        className="chat-message__attachment-download-btn"
                        onClick={handleDownload}
                        disabled={downloading}
                        title="Скачать изображение"
                        aria-label="Скачать изображение"
                    >
                        {downloading ? '⏳' : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                 fill="none" stroke="currentColor" strokeWidth="2"
                                 strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                        )}
                    </button>
                </div>
                <ImagePreviewModal
                    open={previewOpen}
                    onClose={() => setPreviewOpen(false)}
                    imageUrl={imageUrl}
                    alt={fileInfo?.filename || 'Изображение'}
                />
            </>
        );
    }

    // Fallback: обычный файл с кнопкой скачивания
    return (
        <div className="chat-message__attachment chat-message__attachment--file">
            <button
                type="button"
                className="chat-message__attachment-file"
                onClick={handleDownload}
                disabled={downloading}
                title={fileInfo?.filename || 'Файл'}
            >
                <FileIcon className="chat-message__attachment-icon" />
                <span className="chat-message__attachment-filename">{fileInfo?.filename || 'Файл'}</span>
                {downloading && <span className="chat-message__attachment-download-spinner">⏳</span>}
            </button>
        </div>
    );
}

/**
 * Бар реакций под сообщением
 * V2: MessageResponse.reactions — List<MessageReactionResponse>
 *
 * Нормализует реакции: один пользователь — одна реакция.
 * Если сервер вернул несколько реакций от одного пользователя,
 * оставляется только последняя.
 */
function ReactionsBar({ reactions, currentProfileId, onToggleReaction }) {
    if (!reactions || reactions.length === 0) return null;

    // Нормализуем: одна реакция на пользователя
    const normalized = normalizeReactions(reactions);
    if (normalized.length === 0) return null;

    // Группируем реакции по эмодзи
    const grouped = normalized.reduce((acc, r) => {
        if (!acc[r.emoji]) acc[r.emoji] = { count: 0, reactedByMe: false };
        acc[r.emoji].count++;
        if (r.userId === currentProfileId || r.profileId === currentProfileId) {
            acc[r.emoji].reactedByMe = true;
        }
        return acc;
    }, {});

    return (
        <div className="chat-message__reactions">
            {Object.entries(grouped).map(([emoji, info]) => (
                <button
                    key={emoji}
                    className={`chat-message__reaction ${info.reactedByMe ? 'chat-message__reaction--mine' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleReaction?.(emoji, info.reactedByMe);
                    }}
                    title={info.reactedByMe ? 'Убрать реакцию' : 'Поставить реакцию'}
                >
                    <span className="chat-message__reaction-emoji">{emoji}</span>
                    <span className="chat-message__reaction-count">{info.count}</span>
                </button>
            ))}
        </div>
    );
}

/**
 * Превью ответа на сообщение
 * V2: MessageResponse.replyToMessageId — UUID родительского сообщения
 */
function ReplyPreview({ replyMessage, isOwn, loading }) {
    if (loading) {
        return (
            <div className={`chat-message__reply-preview ${isOwn ? 'chat-message__reply-preview--own' : ''}`}>
                <div className="chat-message__reply-line" />
                <div className="chat-message__reply-content">
                    <span className="chat-message__reply-author">Загрузка…</span>
                </div>
            </div>
        );
    }

    if (!replyMessage) return null;

    const authorName = replyMessage.sender?.displayName
        || replyMessage.senderName
        || replyMessage.senderDisplayName
        || 'Пользователь';
    const previewText = replyMessage.isDeleted
        ? '[Сообщение удалено]'
        : (replyMessage.content || '[Вложение]');

    return (
        <div className={`chat-message__reply-preview ${isOwn ? 'chat-message__reply-preview--own' : ''}`}>
            <div className="chat-message__reply-line" />
            <div className="chat-message__reply-content">
                <span className="chat-message__reply-author">{authorName}</span>
                <span className="chat-message__reply-text">{previewText}</span>
            </div>
        </div>
    );
}

const QUICK_REACTIONS = ['👍', '❤️', '🔥', '😂', '😮', '👎'];

/**
 * Превью пересланного сообщения.
 * Показывает "Переслано от {имя}" над контентом сообщения.
 * V2: MessageResponse.forwardedFromMessageId, forwardedFromSenderName, forwardedFromSender
 */
function ForwardedPreview({ message }) {
    const forwardedFromId = message?.forwardedFromMessageId;
    if (!forwardedFromId) return null;

    const senderName = message?.forwardedFromSenderName
        || (typeof message?.forwardedFromSender === 'object'
            ? (message.forwardedFromSender?.displayName
                || message.forwardedFromSender?.name
                || message.forwardedFromSender?.username)
            : null)
        || 'Неизвестно';

    return (
        <div className="chat-message__forwarded">
            <span className="chat-message__forwarded-icon">↪</span>
            <span className="chat-message__forwarded-text">
                Переслано от <span className="chat-message__forwarded-author">{senderName}</span>
            </span>
        </div>
    );
}

/**
 * Индикатор статуса собственного сообщения (галочки).
 * - sending: часы ⏳ (обрабатывается отдельно в renderContent)
 * - sent: одна серая галочка ✓
 * - read: двойная синяя галочка ✓✓
 */
function MessageStatus({ status }) {
    if (status === 'sending') return null; // рендерится через pending-icon

    if (status === 'sent') {
        return (
            <svg className="chat-message__status-icon" width="14" height="14" viewBox="0 0 16 16"
                 fill="none" stroke="currentColor" strokeWidth="1.5"
                 strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 8 6.5 11.5 13 4.5" />
            </svg>
        );
    }

    if (status === 'read') {
        return (
            <svg className="chat-message__status-icon chat-message__status-icon--read"
                 width="18" height="14" viewBox="0 0 20 16" fill="none" stroke="currentColor"
                 strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="2 8 5.5 11.5 12 4.5" />
                <polyline points="7 8 10.5 11.5 17 4.5" />
            </svg>
        );
    }

    return null;
}

/**
 * Изображение стикера в сообщении.
 * Поддерживает два режима:
 * - Локальный путь (/stickers/...) — прямой <img> без авторизации
 * - fileId (UUID) — загрузка через filesApi с кэшированием blob
 */
/**
 * Компонент для отображения GIF-изображения.
 * Загружает файл через filesApi.downloadImage (с аутентификацией)
 * и создаёт blob URL для отображения.
 * Кеширует blob URL через getCachedBlobUrl/createBlobUrl.
 */
function GifImage({ fileId, alt }) {
    const [url, setUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const isLocal = typeof fileId === 'string' && fileId.startsWith('/');

    useEffect(() => {
        if (!fileId) {
            setLoading(false);
            setError(true);
            return;
        }

        // Локальный путь — прямой URL
        if (isLocal) {
            setUrl(fileId);
            setLoading(false);
            return;
        }

        let cancelled = false;
        const load = async () => {
            const cached = getCachedBlobUrl(fileId);
            if (cached) {
                if (!cancelled) { setUrl(cached.url); setLoading(false); }
                return;
            }
            try {
                const blob = await filesApi.downloadImage(fileId, { retries: 3, retryDelay: 1500 });
                if (cancelled || !blob || blob.size === 0) {
                    if (!cancelled) setError(true);
                    return;
                }
                const gifBlob = new Blob([blob], { type: blob.type || 'image/gif' });
                const blobUrl = createBlobUrl(fileId, gifBlob);
                if (!cancelled) { setUrl(blobUrl); setLoading(false); }
            } catch (err) {
                if (err.response?.status !== 429) {
                    console.error('[GifImage] Failed to load:', err.message);
                }
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [fileId, isLocal]);

    if (loading) return <div className="chat-message__gif-skeleton">⏳</div>;
    if (error || !url) return <div className="chat-message__gif-skeleton">❌</div>;
    return (
        <img
            src={url}
            alt={alt || 'GIF'}
            className="chat-message__gif-img"
            loading="lazy"
        />
    );
}

/**
 * Компонент для отображения стикера.
 * Загружает файл через filesApi.downloadImage (с аутентификацией)
 * и создаёт blob URL для отображения.
 * Кеширует blob URL через getCachedBlobUrl/createBlobUrl.
 */
function StickerImage({ fileId, alt }) {
    const [url, setUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const isLocal = typeof fileId === 'string' && fileId.startsWith('/');

    useEffect(() => {
        if (!fileId) {
            setLoading(false);
            setError(true);
            return;
        }

        // Локальный путь — прямой URL
        if (isLocal) {
            setUrl(fileId);
            setLoading(false);
            return;
        }

        let cancelled = false;
        const load = async () => {
            const cached = getCachedBlobUrl(fileId);
            if (cached) {
                if (!cancelled) { setUrl(cached.url); setLoading(false); }
                return;
            }
            try {
                const blob = await filesApi.downloadImage(fileId, { retries: 3, retryDelay: 1500 });
                if (cancelled || blob.size === 0) {
                    if (!cancelled) setError(true);
                    return;
                }
                const stickerBlob = new Blob([blob], { type: blob.type || 'image/png' });
                const blobUrl = createBlobUrl(fileId, stickerBlob);
                if (!cancelled) { setUrl(blobUrl); setLoading(false); }
            } catch (err) {
                if (err.response?.status !== 429) {
                    console.error('[StickerImage] Failed to load:', err.message);
                }
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [fileId, isLocal]);

    if (loading) return <div className="chat-message__sticker-placeholder">⏳</div>;
    if (error || !url) return <div className="chat-message__sticker-placeholder">❌</div>;
    return <img src={url} alt={alt || 'Стикер'} className="chat-message__sticker-img" loading="lazy" />;
}

/**
 * Сообщение в чате
 * V2: MessageResponse — sender (PublicProfileResponse), sentAt, content,
 * isDeleted, updatedAt, replyToMessageId, seenBy, attachments, invitationAttachments, reactions
 *
 * Также поддерживает MessageWebSocketResponse (senderId, senderDisplayName, sentAt, seenBy)
 *
 * @param {Object} message - Объект сообщения
 * @param {boolean} isOwn - Флаг собственного сообщения
 * @param {string} [currentProfileId] - ID профиля текущего пользователя (для реакций)
 * @param {Object} [replyMessage] - Родительское сообщение для отображения превью ответа
 * @param {Function} [onReply] - Колбэк для ответа на сообщение
 * @param {Function} [onEdit] - Колбэк для редактирования сообщения
 * @param {Function} [onDelete] - Колбэк для удаления сообщения
 * @param {Function} [onReact] - Колбэк для реакции (emoji, isRemoving)
 * @param {Function} [onForward] - Колбэк для пересылки сообщения
 * @param {Function} [onBookmark] - Колбэк для закладки (message, isBookmarked) => void
 * @param {boolean} [isBookmarked] - Есть ли закладка на сообщении
 */
export default function ChatMessage({
    message,
    isOwn,
    chatType,
    chatId = null,
    isGrouped = false,
    isGroupedLast = false,
    className = "",
    selectionMode = false,
    isSelected = false,
    onSelect,
    onLongPress,
    onInviteAction,
    currentProfileId,
    chatParticipants = [],
    replyMessage,
    onReply,
    onEdit,
    onDelete,
    onReact,
    onForward,
    onPin,
    onUnpin,
    onBookmark,
    isBookmarked = false,
}) {
    const longPressTimer = useRef(null);
    const isDragging = useRef(false);
    const [showReactionPicker, setShowReactionPicker] = useState(false);
    const [contextMenu, setContextMenu] = useState(null); // { x, y }
    const [copyFeedback, setCopyFeedback] = useState(false);
    const reactionBtnRef = useRef(null);
    const navigate = useNavigate();

    // Переход на страницу профиля отправителя
    const handleProfileClick = useCallback((e) => {
        e.stopPropagation();
        const profileId = message?.senderId
            || (typeof message?.sender === 'object' ? message.sender.id : null);
        if (profileId) navigate(`/profile/${profileId}`);
    }, [message, navigate]);

    // V2: ленивая загрузка родительского сообщения для превью ответа,
    // если оно не передано из ChatWindow (например, при гонке WS/REST
    // или когда родительское сообщение не загружено на текущей странице)
    const msgReplyToId = message?.replyToMessageId;
    const [resolvedReply, setResolvedReply] = useState(null);
    const [replyLoading, setReplyLoading] = useState(false);

    useEffect(() => {
        if (!msgReplyToId || replyMessage) {
            setResolvedReply(null);
            setReplyLoading(false);
            return;
        }
        let cancelled = false;
        setReplyLoading(true);
        chatsApi.getMessage(msgReplyToId)
            .then(data => {
                if (!cancelled) setResolvedReply(data);
            })
            .catch(() => {
                if (!cancelled) setResolvedReply(null);
            })
            .finally(() => {
                if (!cancelled) setReplyLoading(false);
            });
        return () => { cancelled = true; };
    }, [msgReplyToId, replyMessage]);

    /**
     * Копирование текста сообщения в буфер обмена.
     * Использует navigator.clipboard с fallback на execCommand для старых браузеров.
     */
    const handleCopy = useCallback(async (e) => {
        e?.stopPropagation?.();
        const text = message?.content || message?.text || '';
        if (!text) {
            setContextMenu(null);
            return;
        }
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            // Fallback для старых браузеров / не-secure context
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            try { document.execCommand('copy'); } catch {}
            document.body.removeChild(textarea);
        }
        setCopyFeedback(true);
        setTimeout(() => setCopyFeedback(false), 2000);
        setContextMenu(null);
    }, [message]);

    /**
     * Открыть контекстное меню с корректировкой позиции,
     * чтобы меню не выходило за пределы viewport.
     */
    const openContextMenu = useCallback((clientX, clientY) => {
        const menuWidth = 220;
        const menuHeight = 340;
        const x = Math.min(clientX, window.innerWidth - menuWidth - 8);
        const y = Math.min(clientY, window.innerHeight - menuHeight - 8);
        setContextMenu({ x: Math.max(8, x), y: Math.max(8, y) });
    }, []);

    // Закрытие контекстного меню по клику вне, Escape или скроллу
    useEffect(() => {
        if (!contextMenu) return;
        const close = () => setContextMenu(null);
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') close();
        };
        // Небольшая задержка чтобы не закрыть сразу тем же кликом, что открыл
        const timer = setTimeout(() => {
            document.addEventListener('click', close);
            document.addEventListener('contextmenu', close);
            document.addEventListener('keydown', handleKeyDown);
            // passive: true — не блокируем скролл, просто закрываем меню
            document.addEventListener('scroll', close, { capture: true, passive: true });
        }, 0);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('click', close);
            document.removeEventListener('contextmenu', close);
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('scroll', close, { capture: true, passive: true });
        };
    }, [contextMenu]);

    const handlePointerDown = useCallback((e) => {
        isDragging.current = false;
        if (selectionMode) return;
        const touch = e.touches?.[0];
        const x = touch ? touch.clientX : e.clientX;
        const y = touch ? touch.clientY : e.clientY;
        longPressTimer.current = setTimeout(() => {
            if (!isDragging.current) {
                // Длинное нажатие → включаем режим выбора (как в Telegram/WhatsApp)
                // Контекстное меню доступно через правый клик
                onLongPress?.(message);
            }
        }, 600);
    }, [selectionMode, onLongPress, message]);

    const handlePointerUp = useCallback(() => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
    }, []);

    const handlePointerMove = useCallback(() => {
        isDragging.current = true;
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
    }, []);

    const handleClick = useCallback((e) => {
        if (selectionMode) {
            e.preventDefault();
            e.stopPropagation();
            onSelect?.(message);
        }
    }, [selectionMode, onSelect, message]);

    const handleContextMenu = useCallback((e) => {
        e.preventDefault();
        if (selectionMode) {
            onSelect?.(message);
            return;
        }
        openContextMenu(e.clientX, e.clientY);
    }, [selectionMode, onSelect, message, openContextMenu]);

    // Мемоизация тяжёлых операций парсинга — только при изменении текста
    // (должно быть до раннего возврата, чтобы соблюсти правила хуков)
    const messageText = message?.content || message?.text || '';
    const renderedMarkdown = useMemo(
        () => messageText ? renderMarkdownLite(messageText) : null,
        [messageText]
    );
    const extractedUrls = useMemo(
        () => (message && !message.isDeleted && messageText && !message._pending)
            ? extractUrls(messageText)
            : [],
        [messageText, message?.isDeleted, message?._pending]
    );

    // Аватар отправителя: UUID файла → Blob URL (с auth-токеном)
    // Должно быть до раннего возврата для соблюдения правил хуков
    const _senderData = typeof message?.sender === 'object' ? message?.sender : null;
    const _avatarFileId = _senderData?.avatar
        || _senderData?.avatarFileId
        || _senderData?.profile?.avatar
        || null;
    const _avatarBlobUrl = useAvatarUrl(_avatarFileId);

    if (!message) return null;

    const {
        content, text, sentAt, createdAt, updatedAt,
        sender, senderId, senderDisplayName,
        attachments, attachmentIds: msgAttachmentIds,
        invitationAttachments,
        isDeleted, replyToMessageId,
        reactions
    } = message;

    // V2: MessageWebSocketResponse использует senderId + senderDisplayName вместо sender-объекта
    const senderData = typeof sender === 'object' ? sender : null;
    const effectiveSenderName = senderData?.displayName
        || senderData?.profile?.displayName
        || senderDisplayName
        || senderData?.name
        || senderData?.username
        || (isOwn ? 'Я' : 'Unknown');

    // Аватар: blob URL из хука (загружен до раннего возврата)
    const effectiveSenderAvatar = _avatarBlobUrl
        || senderData?.avatarUrl
        || senderData?.profile?.avatarUrl
        || '/default-avatar.png';

    const timestamp = sentAt || createdAt;
    const timeStr = timestamp
        ? new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : '';
    const isEdited = updatedAt && timestamp && new Date(updatedAt).getTime() !== new Date(timestamp).getTime();
    const isDeletedMsg = isDeleted === true;

// Тип сообщения: STICKER рендерится без bubble, как крупное изображение
    const isStickerMessage = message.type === 'STICKER' && !isDeletedMsg;
    // Тип сообщения: GIF — рендерится как крупное изображение без bubble
    // Проверяем и type, и mime-тип вложений (если сервер не сохраняет type: 'GIF')
    const isGifMessage = (() => {
        if (isDeletedMsg) return false;
        if (message.type === 'GIF') return true;
        // Fallback: проверяем mime-тип вложений
        const atts = Array.isArray(attachments) ? attachments : [];
        return atts.some(a => {
            const mime = a.mimeType || a.contentType || '';
            return mime.includes('image/gif');
        });
    })();
    // Тип сообщения: POLL — рендерится внутри bubble как опрос
    const isPollMessage = message.type === 'POLL' && !isDeletedMsg;
    // Сообщение без bubble — стикер или GIF
    const isNoBubbleMessage = isStickerMessage || isGifMessage;

    // Статус собственного сообщения: sending / sent / read
    const ownMessageStatus = (() => {
        if (message._pending) return 'sending';
        const seenByList = message.seenBy;
        if (!Array.isArray(seenByList) || seenByList.length === 0) return 'sent';
        // Есть ли в seenBy кто-то кроме отправителя
        const othersSeen = seenByList.some(s => {
            const seenId = typeof s === 'string' ? s : (s?.id || s?.userId || s?.profileId);
            return seenId !== currentProfileId;
        });
        return othersSeen ? 'read' : 'sent';
    })();

    // Нормализация вложений (V2: attachments — List<String> UUID)
    const normalizeAttachments = () => {
        if (Array.isArray(attachments) && attachments.length > 0) {
            return attachments.map(att => {
                if (typeof att === 'string') {
                    return { fileId: att, type: 'FILE', fileName: 'Файл', mimeType: '' };
                }
                return {
                    fileId: att.fileId || att.id || att,
                    type: att.type || att.fileType || 'FILE',
                    fileName: att.fileName || att.filename || 'Файл',
                    mimeType: att.mimeType || att.contentType || ''
                };
            });
        }
        if (Array.isArray(msgAttachmentIds) && msgAttachmentIds.length > 0) {
            return msgAttachmentIds.map(id => ({ fileId: id, type: 'FILE' }));
        }
        return [];
    };

    const attachmentList = normalizeAttachments();

    // V2: invitationAttachments — отдельное поле (InvitationAttachmentResponse[])
    const inviteList = Array.isArray(invitationAttachments) ? invitationAttachments : [];

    const handleQuickReaction = (emoji) => {
        setShowReactionPicker(false);
        // Один пользователь = одна реакция.
        // Если у пользователя уже есть реакция с этим же эмодзи — снимаем её.
        // Если есть реакция с другим эмодзи — ставим новую (старую удалит handleMessageReact).
        const myReaction = findMyReaction(reactions, currentProfileId);
        const isSameEmoji = myReaction && myReaction.emoji === emoji;
        onReact?.(emoji, !!isSameEmoji);
    };

    const renderContent = () => {
        if (isDeletedMsg) {
            return (
                <>
                    <p className="chat-message__text chat-message__text--deleted">[Сообщение удалено]</p>
                    <span className="chat-message__time">{timeStr}</span>
                </>
            );
        }

        // Стикер: крупное изображение без текста
        if (isStickerMessage) {
            return (
                <>
                    {replyToMessageId && (
                        <ReplyPreview
                            replyMessage={replyMessage || resolvedReply}
                            isOwn={isOwn}
                            loading={replyLoading && !replyMessage && !resolvedReply}
                        />
                    )}
                    {attachmentList.length > 0 && (
                        <div className="chat-message__sticker">
                            {attachmentList.map((att, idx) => {
                                const stickerFileId = att?.fileId || att?.id || att;
                                return (
                                    <StickerImage
                                        key={`sticker-${stickerFileId}-${idx}`}
                                        fileId={stickerFileId}
                                        alt={message.stickerId || 'Стикер'}
                                    />
                                );
                            })}
                        </div>
                    )}
                    <div className="chat-message__meta">
                        <span className="chat-message__time">
                            {message._pending && <span className="chat-message__pending-icon">⏳</span>}
                            {timeStr}
                            {isEdited && <span className="chat-message__edited"> (ред.)</span>}
                        </span>
                        {isOwn && !message._pending && chatType === ChatType.DIRECT && (
                            <MessageStatus status={ownMessageStatus} />
                        )}
                        {isOwn && !message._pending && chatType !== ChatType.DIRECT && (
                            <GroupReadReceipt
                                seenBy={message.seenBy}
                                currentProfileId={currentProfileId}
                                participants={chatParticipants}
                            />
                        )}
                    </div>
                </>
            );
        }

        // GIF: крупное изображение с авто-воспроизведением
        if (isGifMessage) {
            return (
                <>
                    {replyToMessageId && (
                        <ReplyPreview
                            replyMessage={replyMessage || resolvedReply}
                            isOwn={isOwn}
                            loading={replyLoading && !replyMessage && !resolvedReply}
                        />
                    )}
                    <div className="chat-message__gif">
                        {attachmentList.length === 0 ? (
                            <div className="chat-message__gif-skeleton">GIF</div>
                        ) : (
                            attachmentList.slice(0, 1).map((att, idx) => {
                                const gifFileId = att?.fileId || att?.id || att;
                                return (
                                    <GifImage
                                        key={`gif-${gifFileId}-${idx}`}
                                        fileId={gifFileId}
                                        alt={message.gifId || 'GIF'}
                                    />
                                );
                            })
                        )}
                    </div>
                    <div className="chat-message__meta">
                        <span className="chat-message__time">
                            {message._pending && <span className="chat-message__pending-icon">⏳</span>}
                            {timeStr}
                            {isEdited && <span className="chat-message__edited"> (ред.)</span>}
                        </span>
                        {isOwn && !message._pending && chatType === ChatType.DIRECT && (
                            <MessageStatus status={ownMessageStatus} />
                        )}
                        {isOwn && !message._pending && chatType !== ChatType.DIRECT && (
                            <GroupReadReceipt
                                seenBy={message.seenBy}
                                currentProfileId={currentProfileId}
                                participants={chatParticipants}
                            />
                        )}
                    </div>
                </>
            );
        }

        const isInviteMessage = messageText?.includes('🎤 Приглашение в дуэль!');

        // Опрос: рендерим PollMessage вместо обычного текста
        if (isPollMessage) {
            return (
                <>
                    {replyToMessageId && (
                        <ReplyPreview
                            replyMessage={replyMessage || resolvedReply}
                            isOwn={isOwn}
                            loading={replyLoading && !replyMessage && !resolvedReply}
                        />
                    )}
                    <PollMessage
                        chatId={chatId || message.chatId}
                        pollId={message.pollId || message.poll?.id}
                        isOwn={isOwn}
                        pollUpdate={message.poll}
                    />
                    <div className="chat-message__meta">
                        <span className="chat-message__time">
                            {message._pending && <span className="chat-message__pending-icon">⏳</span>}
                            {timeStr}
                            {isEdited && <span className="chat-message__edited"> (ред.)</span>}
                        </span>
                        {isOwn && !message._pending && chatType === ChatType.DIRECT && (
                            <MessageStatus status={ownMessageStatus} />
                        )}
                        {isOwn && !message._pending && chatType !== ChatType.DIRECT && (
                            <GroupReadReceipt
                                seenBy={message.seenBy}
                                currentProfileId={currentProfileId}
                                participants={chatParticipants}
                            />
                        )}
                    </div>
                </>
            );
        }

        return (
            <>
                {replyToMessageId && (
                    <ReplyPreview
                        replyMessage={replyMessage || resolvedReply}
                        isOwn={isOwn}
                        loading={replyLoading && !replyMessage && !resolvedReply}
                    />
                )}
                <ForwardedPreview message={message} />
                {isInviteMessage ? (
                    <DuelInviteFromMessage message={message} isOwn={isOwn} onInviteAction={onInviteAction} />
                ) : (
                    messageText && <p className="chat-message__text">{renderedMarkdown}</p>
                )}
                {!isDeletedMsg && messageText && !message._pending && (() => {
                    if (extractedUrls.length === 0) return null;
                    return (
                        <div className="chat-message__link-previews">
                            {extractedUrls.slice(0, 3).map((url, idx) => (
                                <LinkPreview key={idx} url={url} isOwn={isOwn} />
                            ))}
                        </div>
                    );
                })()}
                {inviteList.length > 0 && (
                    <div className="chat-message__attachments">
                        {inviteList.map((inv, idx) => (
                            <MessageAttachment
                                key={`inv-${inv.id || idx}`}
                                attachment={inv}
                                isOwn={isOwn}
                                onInviteAction={onInviteAction}
                            />
                        ))}
                    </div>
                )}
                {attachmentList.length > 0 && (
                    <div className="chat-message__attachments">
                        {attachmentList.map((att, idx) => (
                            <MessageAttachment
                                key={`att-${att?.id || att?.fileId || idx}-${idx}`}
                                attachment={att}
                                isOwn={isOwn}
                                onInviteAction={onInviteAction}
                            />
                        ))}
                    </div>
                )}
                <div className="chat-message__meta">
                    <span className="chat-message__time">
                        {message._pending && <span className="chat-message__pending-icon">⏳</span>}
                        {timeStr}
                        {isEdited && <span className="chat-message__edited"> (ред.)</span>}
                    </span>
                    {isOwn && !message._pending && chatType === ChatType.DIRECT && (
                        <MessageStatus status={ownMessageStatus} />
                    )}
                </div>
            </>
        );
    };

    const renderActions = () => {
        if (selectionMode || isDeletedMsg || message._pending) return null;

        return (
            <div className={`chat-message__actions ${isOwn ? 'chat-message__actions--own' : ''} ${showReactionPicker ? 'chat-message__actions--picker-open' : ''}`}>
                <button
                    className="chat-message__action-btn"
                    onClick={(e) => { e.stopPropagation(); handleCopy(e); }}
                    title="Копировать текст"
                >
                    ⧉
                </button>
                <button
                    ref={reactionBtnRef}
                    className="chat-message__action-btn"
                    onClick={(e) => { e.stopPropagation(); setShowReactionPicker(!showReactionPicker); }}
                    title="Реакция"
                >
                    😊
                </button>
                <button
                    className="chat-message__action-btn"
                    onClick={(e) => { e.stopPropagation(); onReply?.(message); }}
                    title="Ответить"
                >
                    ↩
                </button>
                {isOwn && (
                    <button
                        className="chat-message__action-btn"
                        onClick={(e) => { e.stopPropagation(); onEdit?.(message); }}
                        title="Редактировать"
                    >
                        ✎
                    </button>
                )}
                {isOwn && (
                    <button
                        className="chat-message__action-btn chat-message__action-btn--danger"
                        onClick={(e) => { e.stopPropagation(); onDelete?.(message); }}
                        title="Удалить"
                    >
                        🗑
                    </button>
                )}

                {showReactionPicker && (
                    <div className="chat-message__reaction-picker-wrapper" onClick={(e) => e.stopPropagation()}>
                        <EmojiPicker
                            anchorRef={reactionBtnRef}
                            mode="reaction"
                            quickReactions={QUICK_REACTIONS}
                            onSelect={handleQuickReaction}
                            onClose={() => setShowReactionPicker(false)}
                            className="chat-message__reaction-picker"
                        />
                    </div>
                )}
            </div>
        );
    };

    const wrapperClass = [
        'chat-message',
        isOwn ? 'chat-message--own' : 'chat-message--other',
        isGrouped ? 'chat-message--grouped' : '',
        isGroupedLast ? 'chat-message--grouped-last' : '',
        selectionMode ? 'chat-message--selectable' : '',
        isSelected ? 'chat-message--selected' : '',
        isDeletedMsg ? 'chat-message--deleted' : '',
        isStickerMessage ? 'chat-message--sticker' : '',
        isGifMessage ? 'chat-message--gif' : '',
        message._pending ? 'chat-message--pending' : '',
        className
    ].filter(Boolean).join(' ');

    const messageContent = (
        <>
            {renderActions()}
            {isNoBubbleMessage ? (
                <div className="chat-message__sticker-wrapper">
                    {renderContent()}
                </div>
            ) : (
                <div className="chat-message__bubble">
                    {renderContent()}
                </div>
            )}
            {copyFeedback && (
                <div className="chat-message__copy-feedback">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 8 6.5 11.5 13 4.5" />
                    </svg>
                    Скопировано
                </div>
            )}
            <ReactionsBar
                reactions={reactions}
                currentProfileId={currentProfileId}
                onToggleReaction={(emoji, isRemoving) => onReact?.(emoji, isRemoving)}
            />
            {contextMenu && createPortal(
                <div
                    className="chat-message__context-menu"
                    style={{ position: 'fixed', left: contextMenu.x, top: contextMenu.y }}
                    onClick={(e) => e.stopPropagation()}
                    onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
                >
                    {!isDeletedMsg && !message._pending && onReply && (
                        <button
                            className="chat-message__context-menu-item"
                            onClick={(e) => { e.stopPropagation(); setContextMenu(null); onReply?.(message); }}
                        >
                            <span className="chat-message__context-menu-icon">↩</span>
                            Ответить
                        </button>
                    )}
                    {!isDeletedMsg && !message._pending && onReact && (
                        <button
                            className="chat-message__context-menu-item"
                            onClick={(e) => { e.stopPropagation(); setContextMenu(null); setShowReactionPicker(true); }}
                        >
                            <span className="chat-message__context-menu-icon">😊</span>
                            Реакция
                        </button>
                    )}
                    {!isDeletedMsg && messageText && (
                        <button
                            className="chat-message__context-menu-item"
                            onClick={handleCopy}
                        >
                            <span className="chat-message__context-menu-icon">⧉</span>
                            Копировать текст
                        </button>
                    )}
                    {!isDeletedMsg && !message._pending && onForward && (
                        <button
                            className="chat-message__context-menu-item"
                            onClick={(e) => { e.stopPropagation(); setContextMenu(null); onForward?.(message); }}
                        >
                            <span className="chat-message__context-menu-icon">↪</span>
                            Переслать
                        </button>
                    )}
                    {!isDeletedMsg && !message._pending && (onPin || onUnpin) && (
                        <button
                            className="chat-message__context-menu-item"
                            onClick={(e) => {
                                e.stopPropagation();
                                setContextMenu(null);
                                if (message.isPinned) {
                                    onUnpin?.(message);
                                } else {
                                    onPin?.(message);
                                }
                            }}
                        >
                            <span className="chat-message__context-menu-icon">📌</span>
                            {message.isPinned ? 'Открепить' : 'Закрепить'}
                        </button>
                    )}
                    {!isDeletedMsg && !message._pending && onBookmark && (
                        <button
                            className="chat-message__context-menu-item"
                            onClick={(e) => {
                                e.stopPropagation();
                                setContextMenu(null);
                                onBookmark?.(message, !isBookmarked);
                            }}
                        >
                            <span className="chat-message__context-menu-icon">{isBookmarked ? '🔖' : '🏷️'}</span>
                            {isBookmarked ? 'В закладках' : 'В закладки'}
                        </button>
                    )}
                    {!selectionMode && onLongPress && (
                        <button
                            className="chat-message__context-menu-item"
                            onClick={(e) => { e.stopPropagation(); setContextMenu(null); onLongPress?.(message); }}
                        >
                            <span className="chat-message__context-menu-icon">☑</span>
                            Выбрать
                        </button>
                    )}
                    {isOwn && !isDeletedMsg && !message._pending && onEdit && (
                        <button
                            className="chat-message__context-menu-item"
                            onClick={(e) => { e.stopPropagation(); setContextMenu(null); onEdit?.(message); }}
                        >
                            <span className="chat-message__context-menu-icon">✎</span>
                            Редактировать
                        </button>
                    )}
                    {isOwn && !isDeletedMsg && !message._pending && onDelete && (
                        <button
                            className="chat-message__context-menu-item chat-message__context-menu-item--danger"
                            onClick={(e) => { e.stopPropagation(); setContextMenu(null); onDelete?.(message); }}
                        >
                            <span className="chat-message__context-menu-icon">🗑</span>
                            Удалить
                        </button>
                    )}
                </div>,
                document.body
            )}
        </>
    );

    if (isOwn) {
        return (
            <div
                className={wrapperClass}
                onClick={handleClick}
                onContextMenu={handleContextMenu}
                onMouseDown={handlePointerDown}
                onMouseUp={handlePointerUp}
                onMouseMove={handlePointerMove}
                onTouchStart={handlePointerDown}
                onTouchEnd={handlePointerUp}
                onTouchMove={handlePointerMove}
            >
                <div className="chat-message__content-wrapper">
                    {messageContent}
                </div>
                <div className="chat-message__checkbox">
                    <div
                        className={`chat-message__checkbox-circle ${isSelected ? 'chat-message__checkbox-circle--checked' : ''}`}
                    />
                </div>
            </div>
        );
    }

    return (
        <div
            className={wrapperClass}
            onClick={handleClick}
            onContextMenu={handleContextMenu}
            onMouseDown={handlePointerDown}
            onMouseUp={handlePointerUp}
            onMouseMove={handlePointerMove}
            onTouchStart={handlePointerDown}
            onTouchEnd={handlePointerUp}
            onTouchMove={handlePointerMove}
        >
            <div className="chat-message__checkbox">
                <div
                    className={`chat-message__checkbox-circle ${isSelected ? 'chat-message__checkbox-circle--checked' : ''}`}
                />
            </div>
            <div className="chat-message__avatar">
                {!isGrouped && (
                    <HrnbImage
                        src={effectiveSenderAvatar}
                        alt={effectiveSenderName}
                        size={36}
                        rounded={true}
                        className="chat-message__avatar-img chat-message__avatar-link"
                        onClick={handleProfileClick}
                    />
                )}
            </div>
            <div className="chat-message__content-wrapper">
                {!isGrouped && (
                    <div
                        className="chat-message__author chat-message__author-link"
                        onClick={handleProfileClick}
                    >
                        {effectiveSenderName}
                    </div>
                )}
                {messageContent}
            </div>
        </div>
    );
}
